import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { previewStudentRecurringInvoices } from '@app/services/student';
import { Button } from '@app/components';
import { getAllActiveFranchise } from '@app/services/franchise';
import Select from "react-select";
import CurrencyFormat from 'react-currency-format';
import moment from 'moment';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import fileDownload from 'js-file-download';
import ExcelJS from "exceljs/dist/es5/exceljs.browser";
import Table from '@app/components/datatable/Table';
import { confirmAlert } from 'react-confirm-alert';
import { encodeId } from '@app/helpers/base64url';

const PreviewInvoices = () => {

    const selectRefGen = useRef(null);
    const [invoicesData, setInvoicesData] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams()
    const [franchisesGen, setFranchisesGen] = useState([]);
    const canAccess = useSelector((state: any) => state.auth.permissions.student);
    const [months, setMonths] = useState([]);
    const allMonths = [{id: 1, name:'January'}, {id: 2, name:'February'}, {id: 3, name:'March'}, 
                    {id: 4, name:'April'}, {id: 5, name:'May'}, {id: 6, name:'June'},
                    {id: 7, name:'July'},{id: 8, name:'August'},{id: 9, name:'September'},
                    {id: 10, name:'October'}, {id: 11, name:'November'}, {id: 12, name:'December'}];
    const years = Array.from( { length: 2 }, (_, index) => new Date().getFullYear() - index );
    const navigate = useNavigate()
    const [t] = useTranslation()

    useEffect(() => {
        if(canAccess['preview-invoices'] == false){
            toast.error(t('auth.noAccess'))
            navigate('/')
        }
    }, [])
    
    useEffect(() => {
        if(searchParams.get('studentName'))
            setInvoices(invoicesData.filter((obj) => obj.student_name.toLowerCase().includes(searchParams.get('studentName')) ))
        else
            setInvoices(invoicesData)
    },[searchParams]);

    useEffect(() => {
        getAllActiveFranchiseData()
        setMonths(allMonths.filter((month) => {
            return month.id <= new Date().getMonth() + 1
        }))
    },[]);

    const getAllActiveFranchiseData = async () => {
        try {
            const resp = await getAllActiveFranchise();
            if (resp?.success === true) {
                let arr = [...resp?.collections]
                arr.unshift({id: 'all', name: 'All', enabled_recurring_invoice: true})
                setFranchisesGen(arr.filter((franchise) => franchise.enabled_recurring_invoice == true))
            } else {
                toast.error(resp?.errors[0]?.msg);
            }
        } catch (error: any) {
            toast.error(error?.message || 'Something went wrong');
        }
    };

    const columns = [
        {
            name: 'Franchise',
            selector: (row: { franchise_name: string; }) => row.franchise_name ? row.franchise_name : '---',
            sortable: true,
            sortField: 'franchise_name',
            wrap: true,
        },
        {
            name: 'Student Name',
            selector: (row: { student_name: string;}) => row.student_name,
            cell: (row: { student_id: number, student_name: string;}) => (
                <>
                <Link
                    to={`/student/edit/${encodeId(row.student_id)}`}
                    role="button"
                    target='_blank'
                    state={{ id: row.student_id }}
                    data-toggle="tooltip" title="Edit Student Details"
                    >
                    { row.student_name }
                </Link>
                </>
            ),
            sortable: true,
            sortField: 'student_name',
            wrap: true,
        },
        {
            name: 'Net Amount',
            selector: (row: { net: string, currency: string }) => row.net,
            format: (row: { net: string, currency: string }) => <CurrencyFormat value={ (parseFloat(row.net)).toFixed(2) } decimalScale={2} displayType={'text'} thousandSeparator={true} prefix={ row.currency + ' '} />,
            sortable: true,
            sortField: 'net',
        },
        {
            name: 'VAT Amount',
            selector: (row: { vat: string, currency: string }) => row.vat,
            format: (row: { vat: string, currency: string }) => <CurrencyFormat value={ (parseFloat(row.vat)).toFixed(2) } decimalScale={2} displayType={'text'} thousandSeparator={true} prefix={ row.currency + ' '} />,
            sortable: true,
            sortField: 'vat',
        },
        {
            name: 'Invoice Amount',
            selector: (row: { invoice_amount: string, currency: string }) => row.invoice_amount,
            format: (row: { invoice_amount: string, currency: string }) => <CurrencyFormat value={ (parseFloat(row.invoice_amount)).toFixed(2) } decimalScale={2} displayType={'text'} thousandSeparator={true} prefix={ row.currency + ' '} />,
            sortable: true,
            sortField: 'invoice_amount',
        },
        {
            name: 'Invoice Duration',
            selector: (row: {invoice_duration: string}) => row.invoice_duration,
            sortable: false,
            wrap: true,
        },
    ];

    const handleResetFilter = async () => {
        searchParams.delete('studentName')
        setSearchParams(searchParams)
        setInvoices(invoicesData)
    };

    const previewRecurringInvoices = async (payload) => {
        try {
            setLoading(true)
            const resp = await previewStudentRecurringInvoices(payload);
            if (resp?.success === true) {
                setInvoices(resp?.collections)
                setInvoicesData(resp?.collections)
                setLoading(false)
                // resetForm()
                // selectRefGen.current.setValue('');
                toast.success(resp?.message)
            } else {
                setLoading(false)
                toast.error(resp?.errors[0]?.msg);
            }
        } catch (error: any) {
            setLoading(false)
            toast.error(error?.message || 'Something went wrong');
        }
    };

    const { handleChange, values, handleSubmit, touched, errors, setFieldValue, resetForm } = useFormik({
        initialValues: {
            franchise_id: '',
            year: '',
            month: '',
        },
        validationSchema: Yup.object({
            franchise_id: Yup.string().required('Please select franchise'),
            year: Yup.string().required('Please select year'),
            month: Yup.string().required('Please select month'),
        }),
        onSubmit: (values) => {
            confirmAlert({
                title: 'Preview Invoices',
                message: 'Are you sure you want to preview the invoices?',
                buttons: [
                    {
                        label: 'Yes',
                        onClick: () => previewRecurringInvoices(values)
                    },
                    {
                        label: 'No',
                        onClick: () => {}
                    }
                ]
            });
        },
        enableReinitialize: true,
    });

    const onYearChange = (e) => {
        setFieldValue('year', e.target.value)
        if(e.target.value != ''){
            setMonths(allMonths)
            // if(e.target.value == new Date().getFullYear()){
            //     setMonths(allMonths.filter((month) => {
            //         return month.id <= new Date().getMonth() + 1
            //     }))
            // }else if(e.target.value < new Date().getFullYear()){
            //     setMonths(allMonths)
            // }
        }else{
            setMonths([])
        }
    }

     const exportCSV = async() => {
        try {
            searchParams.set('perPage', '10')

            if((invoices).length === 0){
                toast.error('Records not found.')
                return;
            }

            const workbook = new ExcelJS.Workbook();
            workbook.addWorksheet("Student-Invoices");
            const worksheet = workbook.getWorksheet('Student-Invoices');
            worksheet.columns = [
                { header: "Franchise", key: "franchise_name" },
                { header: "Student Name", key: "student_name" },
                { header: "Net Amount", key: "net" },
                { header: "VAT Amount", key: "vat" },
                { header: "Invoice Amount", key: "invoice_amount" },
                { header: "Invoice Duration", key: "invoice_duration" },
            ];
            
            (invoices).map((row) => {
                row.net = row.net !== null ? (parseFloat(row.net)).toFixed(2) : '---';
                row.vat = row.vat !== null ? (parseFloat(row.vat)).toFixed(2) : '---';
                row.invoice_amount = row.invoice_amount !== null ? (parseFloat(row.invoice_amount)).toFixed(2) : '---';
            })

            worksheet.addRows(invoices);
            let filename = 'Preview-Invoices-' + moment().format('DD-MMM-YYYY') + '.csv';

            workbook.csv.writeBuffer().then(function(buffer) {
                fileDownload(buffer, filename);
            });
        } catch (error: any) {
            toast.error(error?.message || 'Internal Server Error')
        }
    }

    return (
        <div>
            {/* Content Header (Page header) */}
            <section className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1>Preview Invoices</h1>
                        </div>
                        <div className="col-sm-6">
                            <ol className="breadcrumb float-sm-right">
                                <li className="breadcrumb-item"><a href="/">Home</a></li>
                                <li className="breadcrumb-item active">Preview Invoices</li>
                            </ol>
                        </div>
                    </div>
                </div>{/* /.container-fluid */}
            </section>
            {/* Main content */}
            <section className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div style={{ marginBottom: 10 }} className="row">
                                
                            </div>
                            <div className="card">
                                <div className="card-header">
                                    <h3 className="card-title">Preview Invoices</h3>
                                    <div className="col-lg-12">
                                        
                                    </div>
                                </div>
                                {/* /.card-header */}
                                <div className="card-body">
                                    <div className='col-md-12'>
                                        {/* <div className="external-event" style={{backgroundColor: "rgb(248 213 76)", position: 'relative', zIndex: 'auto', left: '0px', top: '0px', width: '13%', fontWeight: 400, cursor:'default'}}>Generate Invoice</div> */}
                                        <div className="card">
                                            <div className="card-body" style={{backgroundColor: 'rgb(248 241 208)'}}>
                                                <form onSubmit={handleSubmit}>
                                                    <div className='row'>
                                                        <div className="col-sm-3">
                                                            <Select
                                                                ref={selectRefGen}
                                                                name="franchiseId"
                                                                placeholder="Search By Franchise"
                                                                options={franchisesGen}
                                                                getOptionValue={(option)=>`${option['id']}`}
                                                                getOptionLabel={(option)=>`${option['name']}`}
                                                                onChange={ (option) => { 
                                                                    if(option){
                                                                        setFieldValue("franchise_id", option.id)
                                                                    }else{
                                                                        setFieldValue("franchise_id", '')
                                                                    }
                                                                }}
                                                                // styles={customStylesSelect}
                                                            />
                                                            {(touched.franchise_id && errors.franchise_id) && (
                                                                <div className="text-danger">
                                                                    {errors.franchise_id}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="col-sm-3">
                                                            <select
                                                                className="form-control"
                                                                name="year"
                                                                onChange={ (e) => { onYearChange(e) }} 
                                                                value={values.year}
                                                            >
                                                                <option value="">Select Year</option>
                                                                {years.map((year) => (
                                                                    <option key={year} value={year}>
                                                                        {year}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            {(touched.year && errors.year) && (
                                                                <div className="text-danger">
                                                                    {errors.year}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="col-sm-3">
                                                            <select
                                                                className="form-control"
                                                                name="month"
                                                                onChange={handleChange} 
                                                                value={values.month}
                                                            >
                                                                <option value="">Select Month</option>
                                                                { months.map((obj) => (
                                                                    <option key={obj.name} value={obj.id}>
                                                                        {obj.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            {(touched.month && errors.month) && (
                                                                <div className="text-danger">
                                                                    {errors.month}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="col-sm-3">
                                                            <Button type="submit" isLoading={isLoading} className="btn btn-primary">Preview Invoices</Button>
                                                        </div>
                                                    </div>
                                                </form>
                                            </div>
                                        </div>
                                    </div>
                                    <hr style={{borderTop: '2px solid rgba(0,0,0,.125)'}}/>
                                    <div className="row mb-2">
                                        <div className="col-sm-3">
                                            <input type="text" name='studentName' className="form-control" value={searchParams.get('studentName') ?? ''} onChange={(e) => {searchParams.set('studentName', e.target.value); setSearchParams(searchParams)}} placeholder="Search By Student Name" />
                                        </div>
                                        <div className='col-sm-4'>
                                            <Button type="submit" isLoading={isLoading} onClick={handleResetFilter} className="btn btn-warning mr-2">Reset Filter</Button>
                                            { canAccess['export-preview-invoices'] &&
                                                <Button type="submit" isLoading={isLoading} onClick={exportCSV} className="btn btn-success"><i className="fas fa-file-csv"></i> Export</Button>
                                            }
                                        </div>
                                    </div>
                                    <Table
                                        columns={columns}
                                        data={invoices}
                                        pagination
                                        progressPending={isLoading}
                                    />
                                </div>
                                {/* /.card-body */}
                            </div>
                        </div>
                        {/* /.col */}
                    </div>
                    {/* /.row */}
                </div>
                {/* /.container-fluid */}
            </section>
            {/* /.content */}
        </div>
    )
};

export default PreviewInvoices;